// @flow
import type { Store } from 'redux';
import deviceType from 'Common/app/getDeviceType';
import isReactNative from '../../app/isReactNative';
import {
  getLineupId,
  createJsonObj,
  sendVmPlusAnalytics,
  sendBillingAnalytics,
  syncAnalyticsEvents,
} from './sendAnalytics';
import { RECORD_FREQUENCY_OPTIONS, DEFAULT_RECORD_SETTINGS } from '../../dvr/types';
import type { State, BaseAction, ThunkAction, Dispatch } from '../../types';
import type { Action as VmAction, VmEvent } from './types';
import { BingeWatchingEvent } from '../sharedAnalytics/types';

const IS_BILLING_FIRST_FRAME_ANALYTICS_SENT = false;
const IS_BILLING_ACTIVE_USER_ANALYTICS_SENT = true;

const handleVmSpecificEvents = (
  action: VmAction,
  store: Store<State, BaseAction | ThunkAction, Dispatch>,
) => {
  switch (action.type) {
    case 'VM_ANALYTICS_PLAYER_EVENT':
    case 'VM_PREVIEW_OPEN_EVENT':
    case 'VM_FRANCHISE_OPEN_EVENT': {
      const state = store.getState();
      const {
        auth: { viewer: { uuid, receivers } = {} },
        app: { activeReceiver: { receiverId: boxId = '' } = {} },
      } = state;

      const json = createJsonObj(action, uuid, boxId, getLineupId(receivers, boxId));

      sendVmPlusAnalytics(json, store);

      break;
    }

    case 'BILLING_FIRST_FRAME_RENDERED': {
      if (IS_BILLING_FIRST_FRAME_ANALYTICS_SENT) {
        sendBillingAnalytics(store, 'dish_streams');
      }
      break;
    }
    case 'BILLING_ACTIVE_USER_SIGN_IN': {
      if (IS_BILLING_ACTIVE_USER_ANALYTICS_SENT) {
        sendBillingAnalytics(store, 'dish_activity');
      }

      break;
    }
    default:
      break;
  }
};

const handleOtherEvents = (
  action: BaseAction,
  store: Store<State, BaseAction | ThunkAction, Dispatch>,
) => {
  const state = store.getState();
  const {
    auth: { viewer: { uuid, receivers } = {} },
    app: { activeReceiver: { receiverId: boxId = '' } = {} },
  } = state;

  switch (action.type) {
    case 'RECEIVE_UPDATE_TIMER': {
      const {
        payload: { updatedTimer },
      } = action;
      const { gtimer_id: id = '' } = updatedTimer || {};
      const eventName = updatedTimer.event_name || '';
      const recordFrequency =
        RECORD_FREQUENCY_OPTIONS[updatedTimer.frequency ?? DEFAULT_RECORD_SETTINGS.frequency];

      const eventObj: VmEvent = {
        event: 'scheduleRecording',
        progamName: eventName,
        echostarId: id.toString(),
        uuid,
        boxId,
        boxLineupId: getLineupId(receivers, boxId),
        recordFrequency,
      };

      sendVmPlusAnalytics(JSON.stringify(eventObj), store);
      break;
    }

    case 'RECEIVE_CREATE_TIMER': {
      const { payload } = action;
      const id = payload.id ? payload.id.toString() : '';
      const eventName = payload.eventName || '';
      const {
        auth: { viewer: { uuid, last_receiver_id: boxId, receivers } = {} },
      } = state;
      const recordFrequency: string =
        RECORD_FREQUENCY_OPTIONS[payload.frequency ?? DEFAULT_RECORD_SETTINGS.frequency];

      const eventObj: VmEvent = {
        event: 'scheduleRecording',
        progamName: eventName,
        echostarId: id,
        uuid,
        boxId,
        boxLineupId: getLineupId(receivers, boxId),
        recordFrequency,
      };

      sendVmPlusAnalytics(JSON.stringify(eventObj), store);

      break;
    }

    case 'ANALYTICS_BINGE_WATCHING_PLAY_NEXT_OR_CANCEL_SELECTED': {
      const {
        auth: { viewer: { uuid } = {} },
      } = state;
      const {
        isCanceled,
        franchiseName,
        episodeName,
        seasonNumber,
        episodeNumber,
        timeLeft,
      } = action;
      const event = isCanceled
        ? BingeWatchingEvent.BINGE_WATCHING_CANCEL_SELECTED
        : BingeWatchingEvent.BINGE_WATCHING_PLAY_NEXT_SELECTED;

      sendVmPlusAnalytics(
        JSON.stringify({
          event,
          ContentTitle: franchiseName,
          progamName: franchiseName,
          EpisodeName: episodeName,
          SeasonNumber: seasonNumber,
          EpisodeNumber: episodeNumber,
          TimeLeft: timeLeft,
          uuid,
        }),
        store,
      );
      break;
    }

    case 'ANALYTICS_BINGE_WATCHING_NO_USER_ACTION': {
      const { franchiseName, episodeName, seasonNumber, episodeNumber } = action;
      sendVmPlusAnalytics(
        JSON.stringify({
          event: BingeWatchingEvent.BINGE_WATCHING_NO_USER_ACTION,
          ContentTitle: franchiseName,
          progamName: franchiseName,
          EpisodeName: episodeName,
          SeasonNumber: seasonNumber,
          EpisodeNumber: episodeNumber,
          uuid,
        }),
        store,
      );
      break;
    }

    case 'ANALYTICS_NUMBER_OF_CONTINUOUS_EPISODES_WATCHED': {
      const { networkName, eventName, count } = action;
      sendVmPlusAnalytics(
        JSON.stringify({
          event: BingeWatchingEvent.NUMBER_OF_CONTINUOUS_EPISODES_WATCHED,
          NetworkName: networkName,
          EventName: eventName,
          Number: count,
        }),
        store,
      );
      break;
    }

    case 'SIGN_IN_DONE':
    case 'SIGN_IN_DONE_CACHED': {
      if (IS_BILLING_ACTIVE_USER_ANALYTICS_SENT && deviceType !== 'BROWSER') {
        const {
          auth: { signInType },
        } = state;
        if (signInType === 'none') {
          sendBillingAnalytics(store, 'dish_activity');
        }
      }
      break;
    }

    case 'STORAGE_READY': {
      if (state.networkInfo.isInternetReachable) {
        syncAnalyticsEvents(store);
      }
      break;
    }

    case 'NETWORK_STATE_CHANGE': {
      const {
        netInfo: { isInternetReachable },
      } = action;

      if (isInternetReachable && state.app.storageIsReady) {
        syncAnalyticsEvents(store);
      }
      break;
    }
    default:
      break;
  }
};

export default (store: Store<State, BaseAction | ThunkAction, Dispatch>) => (next: any => any) => (
  action: BaseAction,
) => {
  if (action.isVmSpecific) {
    handleVmSpecificEvents(action, store);
    if (isReactNative && __DEV__) {
      next(action);
    }
  } else {
    handleOtherEvents(action, store);
    next(action);
  }
};
